import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { LogOut, CalendarCheck, CalendarDays, Users, Sparkles, Plus, Trash2, Pencil, X, Loader2, Download, Mail } from "lucide-react";

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [savingAvail, setSavingAvail] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [leads, setLeads] = useState([]);

  const load = useCallback(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/services?all=true").then((r) => setServices(r.data)).catch(() => {});
    api.get("/availability").then((r) => setAvailability(r.data.days)).catch(() => {});
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (admin) load();
  }, [admin, load]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#556B2F]" /></div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;

  const cancelBooking = async (id) => {
    await api.patch(`/bookings/${id}`, null, { params: { status: "cancelled" } });
    toast.success("Booking cancelled");
    load();
  };
  const deleteBooking = async (id) => {
    await api.delete(`/bookings/${id}`);
    toast.success("Booking deleted");
    load();
  };

  const openNew = () => {
    setEditing({ name: "", description: "", duration_minutes: 60, price: 0, image_url: "", active: true });
    setDialogOpen(true);
  };
  const openEdit = (s) => {
    setEditing({ ...s });
    setDialogOpen(true);
  };
  const saveService = async () => {
    try {
      const payload = { ...editing, duration_minutes: Number(editing.duration_minutes), price: Number(editing.price) };
      if (editing.id) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success("Service saved");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const deleteService = async (id) => {
    await api.delete(`/services/${id}`);
    toast.success("Service deleted");
    load();
  };

  const saveAvailability = async () => {
    setSavingAvail(true);
    try {
      await api.put("/availability", { days: availability });
      toast.success("Availability updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingAvail(false);
    }
  };

  const statCards = stats
    ? [
        { label: "Total bookings", value: stats.total, icon: CalendarCheck },
        { label: "Today", value: stats.today, icon: CalendarDays },
        { label: "Upcoming", value: stats.upcoming, icon: Users },
        { label: "Active services", value: stats.services, icon: Sparkles },
      ]
    : [];

  const statusColor = (s) => (s === "cancelled" ? "text-[#C0714A] bg-[#C0714A]/10" : "text-[#556B2F] bg-[#556B2F]/10");

  const exportLeadsCsv = () => {
    const rows = [["Name", "Email", "Signed up"], ...leads.map((l) => [l.name || "", l.email, l.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "discipline-code-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F3ECDD]">
      <header className="bg-[#FFFFFF] border-b border-[#DAD0BA]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-serif text-2xl text-[#33311F]">InAlign Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#5C5848] hidden sm:inline">{admin.email}</span>
            <button
              data-testid="logout-button"
              onClick={() => { logout(); navigate("/admin/login"); }}
              className="flex items-center gap-2 text-sm text-[#5C5848] hover:text-[#33311F] border border-[#DAD0BA] rounded-full px-4 py-2 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {statCards.map((c) => (
            <div key={c.label} data-testid={`stat-${c.label}`} className="bg-[#FFFFFF] border border-[#DAD0BA] rounded-2xl p-6">
              <c.icon className="w-5 h-5 text-[#556B2F] mb-3" strokeWidth={1.5} />
              <p className="font-serif text-4xl text-[#33311F]">{c.value}</p>
              <p className="text-sm text-[#5C5848] mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="bg-[#E9E0CC] rounded-full p-1">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-full">Bookings</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services" className="rounded-full">Services</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability" className="rounded-full">Availability</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-full">Guide Leads</TabsTrigger>
          </TabsList>

          {/* Bookings */}
          <TabsContent value="bookings" className="mt-8">
            <div className="bg-[#FFFFFF] border border-[#DAD0BA] rounded-2xl overflow-hidden">
              {bookings.length === 0 ? (
                <p className="p-8 text-center text-[#5C5848]">No bookings yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#E9E0CC]/50 text-left text-[#5C5848]">
                    <tr>
                      <th className="p-4 font-medium">Client</th>
                      <th className="p-4 font-medium">Service</th>
                      <th className="p-4 font-medium">Date</th>
                      <th className="p-4 font-medium">Time</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-[#DAD0BA]">
                        <td className="p-4">
                          <p className="text-[#33311F] font-medium">{b.customer_name}</p>
                          <p className="text-[#5C5848] text-xs">{b.customer_email}</p>
                        </td>
                        <td className="p-4 text-[#33311F]">{b.service_name}</td>
                        <td className="p-4 text-[#5C5848]">{b.date}</td>
                        <td className="p-4 text-[#5C5848]">{b.time}</td>
                        <td className="p-4"><span className={`px-3 py-1 rounded-full text-xs ${statusColor(b.status)}`}>{b.status}</span></td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {b.status !== "cancelled" && (
                              <button data-testid={`cancel-booking-${b.id}`} onClick={() => cancelBooking(b.id)} title="Cancel" className="p-2 hover:bg-[#E9E0CC] rounded-lg text-[#C0714A]">
                                <X className="w-4 h-4" />
                              </button>
                            )}
                            <button data-testid={`delete-booking-${b.id}`} onClick={() => deleteBooking(b.id)} title="Delete" className="p-2 hover:bg-[#E9E0CC] rounded-lg text-[#5C5848]">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* Services */}
          <TabsContent value="services" className="mt-8">
            <div className="flex justify-end mb-4">
              <button data-testid="add-service-button" onClick={openNew} className="flex items-center gap-2 bg-[#556B2F] hover:bg-[#44531F] text-white rounded-full px-5 py-2.5 text-sm transition-colors">
                <Plus className="w-4 h-4" /> Add service
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {services.map((s) => (
                <div key={s.id} data-testid={`admin-service-${s.id}`} className="bg-[#FFFFFF] border border-[#DAD0BA] rounded-2xl p-5 flex gap-4">
                  <img src={s.image_url} alt={s.name} className="w-20 h-20 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <h3 className="font-serif text-xl text-[#33311F]">{s.name}</h3>
                      {!s.active && <span className="text-xs text-[#C0714A]">inactive</span>}
                    </div>
                    <p className="text-xs text-[#5C5848] mt-1 line-clamp-2">{s.description}</p>
                    <p className="text-sm text-[#5C5848] mt-2">{s.duration_minutes} min · ${s.price}</p>
                    <div className="flex gap-2 mt-3">
                      <button data-testid={`edit-service-${s.id}`} onClick={() => openEdit(s)} className="flex items-center gap-1 text-xs border border-[#DAD0BA] rounded-full px-3 py-1.5 hover:bg-[#E9E0CC]">
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button data-testid={`delete-service-${s.id}`} onClick={() => deleteService(s.id)} className="flex items-center gap-1 text-xs border border-[#DAD0BA] rounded-full px-3 py-1.5 hover:bg-[#E9E0CC] text-[#C0714A]">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Availability */}
          <TabsContent value="availability" className="mt-8">
            {availability && (
              <div className="bg-[#FFFFFF] border border-[#DAD0BA] rounded-2xl p-6 max-w-2xl">
                <div className="space-y-4">
                  {WEEKDAYS.map((wd) => (
                    <div key={wd} data-testid={`avail-${wd}`} className="flex items-center gap-4 py-2 border-b border-[#DAD0BA] last:border-0">
                      <div className="flex items-center gap-3 w-40">
                        <Switch
                          data-testid={`avail-toggle-${wd}`}
                          checked={availability[wd].enabled}
                          onCheckedChange={(v) => setAvailability({ ...availability, [wd]: { ...availability[wd], enabled: v } })}
                        />
                        <span className="capitalize text-[#33311F]">{wd}</span>
                      </div>
                      <div className={`flex items-center gap-2 ${availability[wd].enabled ? "" : "opacity-40 pointer-events-none"}`}>
                        <Input type="time" data-testid={`avail-start-${wd}`} value={availability[wd].start} onChange={(e) => setAvailability({ ...availability, [wd]: { ...availability[wd], start: e.target.value } })} className="w-32" />
                        <span className="text-[#5C5848]">to</span>
                        <Input type="time" data-testid={`avail-end-${wd}`} value={availability[wd].end} onChange={(e) => setAvailability({ ...availability, [wd]: { ...availability[wd], end: e.target.value } })} className="w-32" />
                      </div>
                    </div>
                  ))}
                </div>
                <button data-testid="save-availability" onClick={saveAvailability} disabled={savingAvail} className="mt-6 flex items-center gap-2 bg-[#556B2F] hover:bg-[#44531F] text-white rounded-full px-6 py-2.5 text-sm transition-colors disabled:opacity-50">
                  {savingAvail && <Loader2 className="w-4 h-4 animate-spin" />} Save availability
                </button>
              </div>
            )}
          </TabsContent>

          {/* Guide Leads */}
          <TabsContent value="leads" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-[#5C5848]">{leads.length} {leads.length === 1 ? "person has" : "people have"} downloaded The Discipline Code.</p>
              {leads.length > 0 && (
                <button data-testid="export-leads" onClick={exportLeadsCsv} className="flex items-center gap-2 border border-[#DAD0BA] rounded-full px-4 py-2 text-sm hover:bg-[#E9E0CC] transition-colors">
                  <Download className="w-4 h-4" /> Export CSV
                </button>
              )}
            </div>
            <div className="bg-[#FFFFFF] border border-[#DAD0BA] rounded-2xl overflow-hidden">
              {leads.length === 0 ? (
                <div className="p-10 text-center">
                  <Mail className="w-8 h-8 text-[#556B2F] mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-[#5C5848]">No sign-ups yet. Share your Free Guide section to start collecting emails.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#E9E0CC]/60 text-left text-[#5C5848]">
                    <tr>
                      <th className="p-4 font-medium">Name</th>
                      <th className="p-4 font-medium">Email</th>
                      <th className="p-4 font-medium">Signed up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l, i) => (
                      <tr key={l.email + i} data-testid={`lead-row-${i}`} className="border-t border-[#DAD0BA]">
                        <td className="p-4 text-[#33311F] font-medium">{l.name || "—"}</td>
                        <td className="p-4"><a href={`mailto:${l.email}`} className="text-[#556B2F] hover:underline">{l.email}</a></td>
                        <td className="p-4 text-[#5C5848]">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD0BA]" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light text-[#33311F]">{editing?.id ? "Edit service" : "New service"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div>
                <Label>Name</Label>
                <Input data-testid="service-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="service-description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1.5" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Duration (min)</Label>
                  <Input type="number" data-testid="service-duration" value={editing.duration_minutes} onChange={(e) => setEditing({ ...editing, duration_minutes: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Price ($)</Label>
                  <Input type="number" data-testid="service-price" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Image URL</Label>
                <Input data-testid="service-image" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className="mt-1.5" />
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="service-active" checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Active (visible to clients)</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="border border-[#DAD0BA] rounded-full px-5 py-2 text-sm hover:bg-[#E9E0CC]">Cancel</button>
            <button data-testid="save-service" onClick={saveService} className="bg-[#556B2F] hover:bg-[#44531F] text-white rounded-full px-5 py-2 text-sm transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
